import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import { updateAlertMessage } from './commonSlice';
import { classifyError } from '../util/error-classifier.util';

// Define the expected shape of an RTK Query rejected action without using 'any'
interface RtkRejectedAction {
  type: string;
  payload?: {
    status?: string | number;
    originalStatus?: number;
    error?: string;
    data?: unknown;
  };
  error?: {
    name?: string;
    message?: string;
  };
  meta?: {
    aborted?: boolean;
    condition?: boolean;
    rejectedWithValue?: boolean;
  };
}

// Type guard to safely check if an unknown action is an RTK Query rejected action
function isRtkRejectedAction(action: unknown): action is RtkRejectedAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    typeof (action as Record<string, unknown>).type === 'string' &&
    (action as Record<string, string>).type.endsWith('rejected')
  );
}

/**
 * Log a warning and show a toast!
 * This middleware catches all rejected RTK Query actions globally.
 * It ensures we handle client-side network failures identically
 * regardless of whether the request originated from `secure.ts` or `api.admin.ts`.
 */
export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action: unknown) => {
    // Use the strict type guard instead of casting to 'any'
    if (isRtkRejectedAction(action)) {
      // Ignore intentional cancellations (e.g., user navigated away, component unmounted)
      if (action.meta?.aborted || action.meta?.condition) {
        return next(action);
      }

      const errData = action.payload;
      const errorObj = action.error;

      // 4. Observability: Log a structured error for maintainers
      console.error('RTK Query Error:', { action, status: errData?.status || errorObj?.name });

      // 7. DRY: Rely on our central classification utility
      const classification = classifyError(errData || errorObj);

      if (classification === 'TIMEOUT_ERROR') {
         api.dispatch(updateAlertMessage({ 
            status: 'error', 
            message: 'The execution took too long to complete. Please try again.',
            isNetworkError: true,
            timeout: null,
            isManualDismiss: true
         }));
      }
      
      else if (classification === 'NETWORK_ERROR') {
         let uiMessage = 'A network error occurred.';
         
         if (!navigator.onLine) {
            uiMessage = 'You are currently offline. Please reconnect to the internet to continue.';
         } else {
            uiMessage = 'Your connection was interrupted or your network changed. Please try again.';
         }

         api.dispatch(updateAlertMessage({ 
            status: 'error', 
            message: uiMessage,
            isNetworkError: true,
            timeout: null,
            isManualDismiss: true
         }));
      }

      else if (classification === 'SERVER_ERROR' || classification === 'PARSING_ERROR') {
        let message = 'The server encountered a problem. Please try again later.';
        
        if (classification === 'PARSING_ERROR' && errData?.originalStatus !== 502 && errData?.originalStatus !== 503 && errData?.originalStatus !== 504) {
            message = 'We received an unexpected response from the server. Please try again.';
        }
        
        api.dispatch(updateAlertMessage({ 
            status: 'error', 
            message: message,
            isNetworkError: true, // Engage Priority Lock for Server Crashes too
            timeout: null,
            isManualDismiss: true
        }));
      }
    }

    return next(action);
  };
